/**
 * UpgradeModal Component
 * 
 * Modal exibido quando usuário atinge limite de feature
 * - Opção de upgrade para plano superior
 * - Opção de compra avulsa (SOS credits)
 * - Comparação de benefícios
 * 
 * Fase 5 - UI de Pagamentos
 */

import { AlertTriangle, Crown, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface UpgradeModalProps {
  featureKey: string;
  featureName: string;
  currentPlan: 'free' | 'basic' | 'premium';
  usage: { used: number; limit: number };
  onClose: () => void;
}

export function UpgradeModal({ featureKey, featureName, currentPlan, usage, onClose }: UpgradeModalProps) {
  const navigate = useNavigate();

  // Determinar plano recomendado
  const recommendedPlan = currentPlan === 'free' ? 'basic' : 'premium';

  // Features do plano recomendado
  const upgradeFeatures = {
    basic: [
      '5 membros na família (+2)',
      '3 consultas SOS/mês',
      'Análise de prescrição ilimitado',
      'Suporte por email'
    ],
    premium: [
      '10 membros na família (+5)',
      'Consultas SOS ilimitadas',
      'Análise de prescrição ilimitado',
      'Suporte prioritário'
    ]
  };

  const prices = {
    basic: 'R$ 9,90/mês',
    premium: 'R$ 14,90/mês'
  };

  function handleUpgrade() {
    navigate(`/payment?plan=${recommendedPlan}`);
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-xl font-bold">Limite Atingido</h3>
              <p className="text-gray-600 text-sm mt-1">
                Você atingiu o limite de <strong>{featureName}</strong>
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {featureKey === 'prescription_analysis' 
                  ? `Período usado: ${usage.used}/${usage.limit} dias`
                  : `Usado: ${usage.used}/${usage.limit === Infinity ? '∞' : usage.limit}`
                }
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-gray-700 mb-6">
          Para continuar usando, faça upgrade para o plano {recommendedPlan === 'basic' ? 'Básico' : 'Premium'}:
        </p>

        {/* Opção de Upgrade */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl mb-4 border-2 border-purple-200">
          <div className="flex items-center gap-2 mb-3">
            <Crown className="w-6 h-6 text-purple-600" />
            <h4 className="font-bold text-lg">
              Upgrade para {recommendedPlan === 'basic' ? 'Básico' : 'Premium'}
            </h4>
          </div>

          <p className="text-lg font-semibold text-purple-900 mb-4">
            {prices[recommendedPlan]}
          </p>

          <ul className="space-y-2 mb-4">
            {upgradeFeatures[recommendedPlan].map((feature, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={handleUpgrade}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all"
          >
            Fazer Upgrade Agora
          </button>
        </div>

        {/* Opção Avulsa (apenas para SOS) */}
        {featureKey === 'sos_consultation' && (
          <>
            <div className="text-center text-sm text-gray-500 my-3">OU</div>

            <div className="bg-blue-50 p-5 rounded-xl border border-blue-200">
              <h4 className="font-bold mb-2">💡 Comprar Crédito Avulso</h4>
              <p className="text-sm text-gray-600 mb-3">
                R$ 9,90 por consulta
              </p>
              <button
                onClick={() => {
                  navigate('/purchase-sos');
                  onClose();
                }}
                className="w-full py-2 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all"
              >
                Comprar 1 Consulta
              </button>
            </div>
          </>
        )}

        {/* Botão Cancelar */}
        <button
          onClick={onClose}
          className="w-full py-3 text-gray-600 hover:text-gray-800 mt-4 font-medium"
        >
          Agora Não
        </button>
      </div>
    </div>
  );
}
